import { FX } from './fx.js';
/**
 * UI.RewardScene — the shared visual vocabulary of Moonfall's celebration
 * screens.
 *
 * The starter grant, the invitation brief and the invitation payout are three
 * beats of ONE ceremony, so they must be built from the same parts rather than
 * three lookalike implementations that drift. Reported: the invite welcome was
 * "very, very ugly" precisely because it was hand-rolled next to a screen the
 * owner called "absolutely beautiful".
 *
 * This module owns the pieces that carry that look — the moon mark, the spark
 * field — and the FLIP that sends a dismissed screen into the surface where its
 * progress continues to live. It renders nothing on its own and decides
 * nothing; callers supply the copy and the numbers.
 */
let RewardScene = {};

RewardScene = (function () {

  function svgElement(name, attributes = {}) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', name);
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  }

  /** The crescent-and-star mark. `starter-reward-*` classes carry its styling. */
  function createMark() {
    const mark = document.createElement('div');
    mark.className = 'starter-reward-mark';
    mark.setAttribute('aria-hidden', 'true');

    const svg = svgElement('svg', { viewBox: '0 0 96 96', focusable: 'false' });
    const halo = svgElement('circle', { cx: '48', cy: '48', r: '43', class: 'starter-reward-halo' });
    const moon = svgElement('path', {
      d: 'M66 19c-18 5-29 23-24 41 3 11 11 19 21 23-5 2-10 3-15 3-21 0-38-17-38-38S27 10 48 10c6 0 12 1 18 4z',
      class: 'starter-reward-moon',
    });
    const star = svgElement('path', {
      d: 'm65 34 4 10 10 4-10 4-4 10-4-10-10-4 10-4z',
      class: 'starter-reward-star',
    });
    const glint = svgElement('path', {
      d: 'm76 17 2 5 5 2-5 2-2 5-2-5-5-2 5-2z',
      class: 'starter-reward-glint',
    });
    svg.append(halo, moon, star, glint);
    mark.appendChild(svg);
    return mark;
  }

  function createSparks() {
    const field = document.createElement('div');
    field.className = 'starter-reward-sparks';
    field.setAttribute('aria-hidden', 'true');
    for (let index = 1; index <= 10; index += 1) {
      const spark = document.createElement('span');
      spark.className = `starter-reward-spark starter-reward-spark--${index}`;
      field.appendChild(spark);
    }
    return field;
  }

  function reducedMotion() {
    if (FX?.reducedMotion?.() === true) return true;
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    } catch (_) {
      return false;
    }
  }

  /** The first target that is actually on screen, so a phone lands on its rail. */
  function firstVisible(selectors) {
    for (const selector of selectors) {
      const node = document.querySelector(selector);
      if (!node || typeof node.getBoundingClientRect !== 'function') continue;
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return node;
    }
    return null;
  }

  /**
   * Send `card` into `target` — the screen visibly BECOMES the thing that now
   * tracks it, instead of evaporating and leaving the player to discover the
   * quest on their own. Resolves once the motion is done; resolves immediately
   * when motion is reduced or unavailable, so a dismissal can never hang.
   */
  function minimizeInto(card, target, { duration = 460 } = {}) {
    const done = Promise.resolve();
    if (!card || !target || reducedMotion() || typeof card.animate !== 'function') return done;

    let from;
    let to;
    try {
      from = card.getBoundingClientRect();
      to = target.getBoundingClientRect();
    } catch (_) {
      return done;
    }
    if (!from?.width || !from?.height || !to?.width || !to?.height) return done;

    /* Scale to the target's WIDTH only. Matching both axes squashes a tall
       dialog into a short rail and reads as a glitch rather than a filing. */
    const scale = Math.max(0.06, Math.min(1, to.width / from.width));
    const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    const dy = (to.top + to.height / 2) - (from.top + from.height / 2);

    let animation;
    try {
      animation = card.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.55}px) scale(${(1 + scale) / 2})`, opacity: 0.92, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0 },
      ], { duration, easing: 'cubic-bezier(0.55, 0, 0.3, 1)', fill: 'forwards' });
    } catch (_) {
      return done;
    }
    return animation.finished.catch(() => {});
  }

  return { createMark, createSparks, minimizeInto, firstVisible, reducedMotion };
}());

export { RewardScene };
